import React, { useEffect, useRef } from 'react';
import { useOverlay, useModal, AriaModalOptions, AriaOverlayProps } from '@react-aria/overlays';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { AriaDialogProps } from '@react-types/dialog';
import { useScrollControl } from './useScrollControl';
import { cn } from '../cn';
import { Button, Grid, GridItem, TGridItemType, Typography, IconClose } from '../index';

export type ModalAriaProps = React.PropsWithChildren<{
  className?: string,
  dialogClassName?: string,
  title: string,
  footerButtons?: React.ReactNode,
  isOpen: boolean,
  showCloseButton?: boolean,
  onClose?: () => void,
  type?: TGridItemType | 'narrower',
  fullHeight?: boolean,
  noBackground?: boolean,
  noHeader?: boolean,
}> & AriaDialogProps & AriaModalOptions & AriaOverlayProps;

export const Modal = (props: ModalAriaProps): JSX.Element => {
  const ref = useRef(null);
  const { overlayProps, underlayProps } = useOverlay(props, ref);
  const { modalProps } = useModal();
  const { dialogProps, titleProps } = useDialog(props, ref);
  const [openDebounceInteraction, setOpenDebounceInteraction] = React.useState(false);
  useEffect(() => {
    if(props.isOpen) {
      const timeout = setTimeout(() => {
        setOpenDebounceInteraction(true);
      }, 150);
      return () => {
        clearTimeout(timeout);
      };
    } else {
      setOpenDebounceInteraction(false);
    }
  }, [props.isOpen]);

  useScrollControl(props.isOpen);

  const propType = props.type || 'narrower';
  const gridType = propType === 'narrower' ? 'narrow' : propType;

  return (
    <div
      {...underlayProps}
      onClick={() => {
        props.isDismissable && props.onClose && props.onClose();
      }}
      className={cn(
        'fixed flex top-0 bottom-0 left-0 right-0 z-[10000] bg-black bg-opacity-80 items-center justify-center',
        !props.isOpen && 'hidden',
        props.className,
        !openDebounceInteraction && 'pointer-events-none',
      )}
    >
      <Grid className='m-0'>
        <GridItem type={gridType}>
          <FocusScope contain restoreFocus autoFocus>
            <div
              className={cn(
                'w-full relative flex flex-col gap-3 overflow-hidden',
                props.noBackground ? '' : 'bg-surface dark:bg-surface-dark',
                propType === 'narrower' && 'max-w-96 m-auto',
                props.fullHeight && 'h-dvh',
                props.dialogClassName,
              )}
              {...overlayProps}
              {...dialogProps}
              {...modalProps}
              ref = {ref}
              onClick={(e) => {e.stopPropagation();}}
              style={{
                boxShadow: props.fullHeight ? undefined : '0px 25px 50px -12px rgba(0, 0, 0, 0.25), 0px 8px 10px -6px rgba(0, 0, 0, 0.1)',
                maxHeight: props.fullHeight ? undefined : 'calc(100dvh - calc(4 * var(--riac-grid-gap)))',
                marginTop: props.fullHeight ? undefined : 'calc(-2 * var(--riac-grid-gap))',
              }}
            >
              {(props.title && !props.noHeader)
                && <div className='border-b-1 border-surface-darker justify-between flex py-3 px-5 items-center gap-3'>
                  <Typography heading size='sm' weight='semibold' {...titleProps}>{props.title}</Typography>
                  {props.showCloseButton
                    && <Button
                      unstyled
                      className={'text-text-lighter hover:text-text-light active:text-text h-8 w-8 items-center flex justify-center'}
                      onPress={props.onClose}
                    >
                      <IconClose className='size-6'/>
                    </Button>
                  }
                </div>
              }
              <div className={cn('overflow-y-auto overflow-x-hidden flex-1', props.noBackground ? '' : 'px-5')}>
                {props.children}
              </div>
              {props.footerButtons
                && <div className={cn(
                  'border-t-1 border-surface-darker py-3 flex gap-3 bg-surface-dark dark:bg-surface-darker px-5 justify-end',
                )}>
                  {props.footerButtons}
                </div>
              || undefined
              }
            </div>
          </FocusScope>
        </GridItem>
      </Grid>
    </div>
  );
};

